import { Fragment, useCallback, useId, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useT } from "../../lib/i18n";
import { cx } from "../../lib/cx";
import { stepperConnectorOn, stepperNodeState, type StepState } from "./model";
import { dsControl, dsFont } from "./styles";

export type StageStep = {
  id: string;
  label: string;
};

type Props = {
  steps: StageStep[];
  /** Zero-based index of the current stage. */
  currentIndex: number;
  /** Bold caption under the track. Omit to show the nodes alone. */
  caption?: string;
  /** Regular "next: …" caption. Omit on the last stage. */
  next?: string;
};

function Check() {
  return (
    <svg
      viewBox="0 0 24 24"
      className="size-2.5"
      fill="none"
      stroke="currentColor"
      strokeWidth="3.2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <path d="M5 12l5 5 9-10" />
    </svg>
  );
}

function stateKey(state: StepState): "ds.stepper.state.done" | "ds.stepper.state.current" | "ds.stepper.state.upcoming" {
  if (state === "done") return "ds.stepper.state.done";
  if (state === "current") return "ds.stepper.state.current";
  return "ds.stepper.state.upcoming";
}

/**
 * One node. Hover and keyboard focus portal a glass tooltip above it so the
 * card and the page overflow cannot clip it. DESIGN.md tile radius (16px)
 * keeps the card small; a 999px radius would turn a wrapped tip into an ellipse.
 */
function StageNode({
  step,
  index,
  state,
}: {
  step: StageStep;
  index: number;
  state: StepState;
}) {
  const t = useT();
  const tipId = useId();
  const ref = useRef<HTMLButtonElement>(null);
  const [hover, setHover] = useState(false);
  const [focus, setFocus] = useState(false);
  const open = hover || focus;
  const [pos, setPos] = useState({ top: 0, left: 0 });
  const tip = t("ds.stepper.tip", {
    n: index + 1,
    label: step.label,
    state: t(stateKey(state)),
  });

  const place = useCallback(() => {
    const rect = ref.current?.getBoundingClientRect();
    if (!rect) return;
    setPos({ top: rect.top - 8, left: rect.left + rect.width / 2 });
  }, []);

  useLayoutEffect(() => {
    if (!open) return;
    place();
    const sync = () => place();
    window.addEventListener("scroll", sync, true);
    window.addEventListener("resize", sync);
    return () => {
      window.removeEventListener("scroll", sync, true);
      window.removeEventListener("resize", sync);
    };
  }, [open, place]);

  return (
    <>
      <button
        ref={ref}
        type="button"
        data-state={state}
        data-testid="ds-stepper-node"
        aria-label={tip}
        aria-describedby={open ? tipId : undefined}
        className={cx(
          dsControl,
          "grid size-[22px] shrink-0 place-items-center rounded-full px-0 py-0 text-ds-badge font-semibold",
          state === "done" && "bg-ds-teal text-white",
          state === "current" && "bg-white text-ds-on-amber shadow-[0_0_0_2px_var(--ds-teal)]",
          state === "later" && "bg-ds-hair text-ds-ink-3",
        )}
        onMouseEnter={() => setHover(true)}
        onMouseLeave={() => setHover(false)}
        onFocus={() => setFocus(true)}
        onBlur={() => setFocus(false)}
      >
        {state === "done" ? <Check /> : index + 1}
      </button>
      {open
        ? createPortal(
            <span
              id={tipId}
              role="tooltip"
              data-testid="ds-stepper-tooltip"
              className="pointer-events-none fixed z-[90] -translate-x-1/2 -translate-y-full whitespace-nowrap rounded-ds-tile bg-ds-glass-strong px-2.5 py-1 font-ds text-ds-meta font-normal text-ds-ink shadow-ds-card backdrop-blur-ds backdrop-saturate-[1.1]"
              style={{ top: pos.top, left: pos.left }}
            >
              {tip}
            </span>,
            document.body,
          )
        : null}
    </>
  );
}

/**
 * Files → Outcome. Done is a teal check, current is a white node with a
 * 2px teal ring, later is a hair node. The track is only the nodes, so a
 * table row can center them with the name, due date and action.
 */
export function StageStepper({ steps, currentIndex, caption, next }: Props) {
  const t = useT();
  const current = steps[currentIndex];
  return (
    <div
      data-ds=""
      data-testid="ds-stepper"
      className={cx(
        dsFont,
        // The 14px column gap is not enough once the action track is narrow.
        // This inset keeps the last node at least 24px from the Due column.
        "min-w-0 max-w-[calc(100%-10px)] self-center",
      )}
    >
      <div
        className="flex min-w-0 items-center"
        role="group"
        aria-label={t("ds.stepper.progress", {
          current: currentIndex + 1,
          total: steps.length,
          label: current?.label ?? caption ?? "",
        })}
      >
        {steps.map((step, index) => {
          const state = stepperNodeState(index, currentIndex);
          return (
            <Fragment key={step.id}>
              <StageNode step={step} index={index} state={state} />
              {index < steps.length - 1 ? (
                <span
                  data-on={stepperConnectorOn(index, currentIndex) ? "true" : "false"}
                  className={cx(
                    "h-[2px] min-w-2 flex-1",
                    stepperConnectorOn(index, currentIndex) ? "bg-ds-teal" : "bg-ds-hair",
                  )}
                />
              ) : null}
            </Fragment>
          );
        })}
      </div>
      {caption ? (
        <p className="m-0 mt-1.5 text-ds-label font-normal text-ds-ink-3">
          <b className="font-semibold text-ds-ink">{caption}</b>
          {next ? <span> · {t("ds.stepper.next", { label: next })}</span> : null}
        </p>
      ) : null}
    </div>
  );
}
